import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/bootstrap.css'
import {initialValues, type MemberShipForm} from './_module'
import {useAppDispatch} from '../../../redux/hooks'
import Loader from 'app/custom/loader/Loader'
import {showErrorMessage} from '_metronic/helpers/swalHelper'
import CustomSelect from 'app/custom/CustomSelect'
import {Prefix} from '_metronic/helpers/jsonData'
import {toAbsoluteUrl} from '_metronic/helpers'
import i18n from 'i18n'
import {OnlineProps, StoreMember} from '../../../redux/Slice/generalSlice'
import ShowSuccessMsg from './ShowSuccessMsg'
import './online-membership.css'

const DEFAULT_PHONE_COUNTRY = 'ch'
const DEFAULT_DIAL_CODE = '+41'

const OnlineMembership = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()

  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(true)
  const [successMsg, setSuccessMsg] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [membershipCategories, setMembershipCategories] = useState<any>([])
  const [municipalities, setMunicipalities] = useState<any>([])

  const validationSchema = Yup.object().shape({
    prefix: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.TITLE')})),
    first_name: Yup.string()
      .trim()
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'’-]+$/, t('VALIDATION.USE_LETTERS'))
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.FIRST_NAME')})),
    last_name: Yup.string()
      .trim()
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'’-]+$/, t('VALIDATION.USE_LETTERS'))
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.LAST_NAME')})),
    address: Yup.string()
      .trim()
      .max(255, t('VALIDATION.MAX_CHARACTER_', {quantity: 255}))
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.ADDRESS')})),
    postal_code: Yup.string()
      .trim()
      .max(20, t('VALIDATION.MAX_CHARACTER_', {quantity: 20}))
      .required(t('VALIDATION.SELECT_', {name: t('LABEL.POSTAL_CODE')})),
    municipality_id: Yup.string().required(
      t('VALIDATION.SELECT_', {name: t('LABEL.MUNICIPALITY')})
    ),
    contact_number: Yup.string()
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.CONTACT_NUMBER')}))
      .matches(/^\+[1-9]\d{7,14}$/, t('VALIDATION.INVALID_NUMBER')),
    email: Yup.string()
      .trim()
      .email(t('VALIDATION.INVALID_EMAIL'))
      .required(t('VALIDATION.REQUIRED_', {name: t('PROFILE.EMAIL')})),

    membership_category_id: Yup.string().required(
      t('VALIDATION.SELECT_', {name: t('LABEL.MEMBERSHIP_CATEOGORY')})
    ),
    confirmation_msg: Yup.boolean().oneOf(
      [true],
      t('VALIDATION.SELECT_', {name: t('LABEL.CONSENT_DATA')})
    ),
  })

  const formik = useFormik<MemberShipForm>({
    initialValues,
    validationSchema,
    onSubmit: async (values, {setSubmitting, resetForm, setErrors, setTouched}) => {
      setLoading(true)
      setFirstName(values.first_name)

      const formData = new FormData()
      formData.append('language', i18n.language)

      Object.entries(values).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, String(item))
          })
          return
        }

        if (value === null || value === undefined) return

        formData.append(key, String(value))
      })

      try {
        const res: any = await dispatch(StoreMember(formData))

        if (res.meta.requestStatus === 'fulfilled') {
          resetForm()
          setSuccessMsg(true)
          return
        }

        const payload = res.payload
        if (payload?.errors) {
          const formErrors: Partial<Record<keyof MemberShipForm, string>> = {}
          const touchedFields: Partial<Record<keyof MemberShipForm, boolean>> = {}
          Object.entries(payload.errors).forEach(([fieldName, messages]) => {
            const normalizedField = fieldName.split('.')[0] as keyof MemberShipForm
            const firstMessage = Array.isArray(messages) ? String(messages[0]) : String(messages)
            formErrors[normalizedField] = firstMessage
            touchedFields[normalizedField] = true
          })

          setErrors(formErrors)
          setTouched(touchedFields, false)
          return
        }

        showErrorMessage(payload?.message ?? t('MESSAGES.SERVER_ERROR'))
      } catch (error) {
        showErrorMessage(t('MESSAGES.SERVER_ERROR'))
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    setLoader(true)
    dispatch(OnlineProps(i18n.language))
      .then((res: any) => {
        const payload = res.payload
        setMembershipCategories(payload.memCat ?? [])
        const translatedCommunes = payload.municipalities?.map((item: any) => ({
          ...item,
          name: item.name === 'LABEL.OTHER' ? t(item.name) : item.name,
        }))
        setMunicipalities(translatedCommunes)
      })
      .finally(() => setLoader(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    window.location.reload()
  }

  const languages = [
    {lang: 'fr', name: 'Français', short: 'FR'},
    {lang: 'de', name: 'Deutsch', short: 'DE'},
    {lang: 'it', name: 'Italiano', short: 'IT'},
    {lang: 'en', name: 'English', short: 'EN'},
    {lang: 'es', name: 'Español', short: 'ES'},
    {lang: 'pt', name: 'Português', short: 'PT'},
    {lang: 'sq', name: 'Shqip', short: 'SQ'},
    {lang: 'tr', name: 'Türkçe', short: 'TR'},
    {lang: 'ar', name: 'العربية', short: 'AR'},
    {lang: 'fa', name: 'فارسی', short: 'FA'},
    {lang: 'ps', name: 'پښتو', short: 'PS'},
    {lang: 'ur', name: 'اردو', short: 'UR'},
    {lang: 'hi', name: 'हिन्दी', short: 'HI'},
  ]

  const fieldError = (name: keyof MemberShipForm) => {
    const error = formik.errors[name]
    const touched = formik.touched[name]

    if (!touched || !error || Array.isArray(error)) return null

    return <div className='invalid-feedback d-block'>{String(error)}</div>
  }

  const inputClass = (name: keyof MemberShipForm) =>
    clsx('form-control form-control-sm', {
      'is-invalid': formik.touched[name] && formik.errors[name],
      'is-valid': formik.touched[name] && !formik.errors[name],
    })

  const prefixOptions = Prefix.map((item) => ({
    ...item,
    name: t(item.name),
  }))

  if (loader) return <Loader />

  if (successMsg) return <ShowSuccessMsg firstName={firstName} />

  return (
    <div className='container-fluid container-md'>
      <div className='card border-0 shadow-sm'>
        <div className='card-header bg-white border-bottom d-flex align-items-center justify-content-between gap-3 px-3 px-md-5 py-3'>
          <img
            src={toAbsoluteUrl('/media/logo/horizon-logo.png')}
            alt='Horizon Genève'
            className='membership-logo'
          />

          {/* Language Dropdown */}
          <div className='dropdown'>
            <button
              className='btn btn-light border d-flex align-items-center gap-2 px-4 py-2'
              type='button'
              data-bs-toggle='dropdown'
              aria-expanded='false'
              style={{
                borderRadius: '8px',
                minWidth: '145px',
              }}
            >
              <i
                className='fa-solid fa-globe'
                style={{
                  color: '#14264b',
                  fontSize: '16px',
                }}
              />

              <span
                style={{
                  color: '#14264b',
                  fontWeight: 600,
                }}
              >
                {t('GLOBAL.LANGUAGE')}
              </span>

              <i
                className='fa-solid fa-chevron-down ms-auto'
                style={{
                  fontSize: '10px',
                  color: '#7e8299',
                }}
              />
            </button>

            <ul
              className='dropdown-menu dropdown-menu-end shadow border-0 py-2'
              style={{
                minWidth: '190px',
                maxHeight: '350px',
                overflowY: 'auto',
                borderRadius: '10px',
              }}
            >
              {languages.map((language) => (
                <li key={language.lang}>
                  <button
                    type='button'
                    className={clsx(
                      'dropdown-item d-flex justify-content-between align-items-center py-2 px-4',
                      {
                        active: i18n.language === language.lang,
                      }
                    )}
                    onClick={() => handleChangeLanguage(language.lang)}
                  >
                    <span>{language.name}</span>

                    <span
                      className='ms-3'
                      style={{
                        fontSize: '11px',
                        opacity: 0.7,
                      }}
                    >
                      {language.short}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className='card-body p-3 p-md-5 p-xl-6'>
          <div className='mb-4 mb-md-5'>
            <h2 className='mb-2 text-center'>{t('LABEL.MEMBERSHIP_FORM')}</h2>
            <label className='form-label fw-bold'>{t('LABEL.JOIN_INTRO')}</label>
            <p className='text-muted mb-0'>{t('LABEL.REQUIRED_FIELD')}</p>
          </div>

          <form noValidate autoComplete='off' onSubmit={formik.handleSubmit}>
            {/* Personal information */}
            <section className='membership-section mb-4 mb-md-5'>
              <div className='section-heading'>
                <span className='section-number'>1</span>
                <div>
                  <h2 className='h5 fw-bold mb-1'>{t('LABEL.PERSONAL_INFO')}</h2>
                </div>
              </div>

              <div className='row g-3 g-lg-4 mt-1'>
                <div className='col-sm-12 col-md-6 col-lg-4'>
                  <label className='form-label fw-bold required'>{t('LABEL.TITLE')}</label>
                  <CustomSelect
                    name='prefix'
                    className='input'
                    value={formik.values.prefix}
                    options={prefixOptions}
                    placeholderText={t('VALIDATION.PLEASE_SELECT_AN_OPTION')}
                    onChange={(obj: any) => formik.setFieldValue('prefix', obj.id)}
                  />
                  {fieldError('prefix')}
                </div>

                <div className='col-sm-12 col-md-6 col-lg-4'>
                  <label className='form-label fw-bold required'>{t('LABEL.FIRST_NAME')}</label>
                  <input
                    type='text'
                    placeholder={t('LABEL.FIRST_NAME')}
                    {...formik.getFieldProps('first_name')}
                    className={clsx(inputClass('first_name'), 'text-capitalize')}
                  />
                  {fieldError('first_name')}
                </div>

                <div className='col-sm-12 col-md-6 col-lg-4'>
                  <label className='form-label fw-bold required'>{t('LABEL.LAST_NAME')}</label>
                  <input
                    type='text'
                    placeholder={t('LABEL.LAST_NAME')}
                    {...formik.getFieldProps('last_name')}
                    className={clsx(inputClass('last_name'), 'text-uppercase')}
                  />
                  {fieldError('last_name')}
                </div>
              </div>
            </section>

            {/* Contact details */}
            <section className='membership-section mb-4'>
              <div className='section-heading'>
                <span className='section-number'>2</span>
                <div>
                  <h2 className='h5 fw-bold mb-1'>{t('LABEL.CONTACT_DETAILS')}</h2>
                </div>
              </div>

              <div className='row g-3 g-lg-4 mt-1'>
                <div className='col-sm-12 col-md-6 col-lg-12'>
                  <input
                    type='text'
                    placeholder={t('LABEL.ADDRESS')}
                    {...formik.getFieldProps('address')}
                    className={inputClass('address')}
                  />
                  {fieldError('address')}
                </div>

                <div className='col-sm-12 col-md-6'>
                  <label className='form-label fw-bold required'>{t('LABEL.POSTAL_CODE')}</label>
                  <input
                    type='text'
                    placeholder={t('LABEL.POSTAL_CODE')}
                    {...formik.getFieldProps('postal_code')}
                    className={inputClass('postal_code')}
                  />
                  {fieldError('postal_code')}
                </div>

                <div className='col-sm-12 col-md-6'>
                  <label className='form-label fw-bold required'>{t('LABEL.MUNICIPALITY')}</label>
                  <CustomSelect
                    name='municipality_id'
                    className='input'
                    value={formik.values.municipality_id}
                    options={municipalities}
                    placeholderText={t('VALIDATION.PLEASE_SELECT_AN_OPTION')}
                    onChange={(obj) => formik.setFieldValue('municipality_id', obj.id)}
                  />
                  {fieldError('municipality_id')}
                </div>

                <div className='col-sm-12 col-md-6'>
                  <label className='form-label fw-bold required'>{t('LABEL.CONTACT_NUMBER')}</label>
                  <div
                    className={clsx('membership-phone', {
                      'phone-invalid':
                        formik.touched.contact_number && formik.errors.contact_number,
                      'phone-valid': formik.touched.contact_number && !formik.errors.contact_number,
                    })}
                  >
                    <PhoneInput
                      country={DEFAULT_PHONE_COUNTRY}
                      value={formik.values.contact_number}
                      preferredCountries={['ch', 'fr', 'sn']}
                      enableSearch
                      searchPlaceholder={t('LABEL.SEARCH_COUNTRY')}
                      countryCodeEditable={false}
                      inputProps={{
                        name: 'contact_number',
                        required: true,
                        autoComplete: 'tel',
                      }}
                      onChange={(phone) => {
                        const digits = phone.replace(/\D/g, '')
                        formik.setFieldValue(
                          'contact_number',
                          digits ? `+${digits}` : DEFAULT_DIAL_CODE
                        )
                      }}
                      onBlur={() => formik.setFieldTouched('contact_number', true)}
                    />
                  </div>
                  {fieldError('contact_number')}
                </div>

                <div className='col-sm-12 col-md-6'>
                  <label className='form-label fw-bold required'>{t('PROFILE.EMAIL')}</label>
                  <input
                    type='email'
                    placeholder='nom@exemple.ch'
                    autoComplete='email'
                    {...formik.getFieldProps('email')}
                    className={inputClass('email')}
                  />
                  {fieldError('email')}
                </div>
              </div>
            </section>

            {/* Membership */}
            <section className='membership-section mb-4'>
              <div className='section-heading'>
                <span className='section-number'>3</span>
                <div>
                  <h2 className='h5 fw-bold mb-1'>{t('LABEL.MEMBERSHIP')}</h2>
                </div>
              </div>
              <div className='row g-3 g-lg-4'>
                <div className='col-sm-12 col-lg-6 pt-4'>
                  <CustomSelect
                    name='membership_category_id'
                    className='input'
                    value={formik.values.membership_category_id}
                    options={membershipCategories}
                    placeholderText={t('VALIDATION.PLEASE_SELECT_AN_OPTION')}
                    onChange={(obj) => formik.setFieldValue('membership_category_id', obj.id)}
                  />
                  {fieldError('membership_category_id')}
                </div>
              </div>
            </section>

            {/* Sponsorship */}
            <section className='membership-section mb-4 mb-md-5'>
              <div className='section-heading'>
                <span className='section-number'>4</span>
                <div>
                  <h2 className='h5 fw-bold mb-1'>{t('LABEL.SPONSORSHIP')}</h2>
                  <p className='text-muted small mb-0'>{t('LABEL.SPONSORSHIP_INFO')}</p>
                </div>
              </div>

              <div className='row g-3 g-lg-4 mt-1'>
                <div className='col-sm-12 col-md-6'>
                  <label className='form-label fw-bold'>{t('LABEL.FULL_NAME')}</label>
                  <input
                    type='text'
                    placeholder={t('LABEL.FULL_NAME')}
                    {...formik.getFieldProps('sponsor_full_name')}
                    className={inputClass('sponsor_full_name')}
                  />
                  {fieldError('sponsor_full_name')}
                </div>
              </div>
            </section>

            {/* Comment */}
            <section className='membership-section mb-4'>
              <div className='section-heading'>
                <span className='section-number'>5</span>
                <div>
                  <h2 className='h5 fw-bold mb-1'>{t('LABEL.ADDITIONAL_COMMENTS')}</h2>
                </div>
              </div>

              <div className='mt-4'>
                <textarea
                  id='comments'
                  rows={5}
                  placeholder={t('LABEL.YOUR_COMMENT')}
                  {...formik.getFieldProps('comments')}
                  className={inputClass('comments')}
                />
                <div className='d-flex justify-content-between gap-3 mt-1'>
                  <span className='form-text ms-auto'>{formik.values.comments.length}/1000</span>
                </div>
              </div>
              <div className='col-12'>
                <label className='col-form-label d-flex align-items-center gap-2'>
                  <input
                    type='checkbox'
                    name='confirmation_msg'
                    className={clsx('form-check-input', {
                      'is-invalid':
                        (formik.touched.confirmation_msg || formik.submitCount > 0) &&
                        formik.errors.confirmation_msg,
                    })}
                    checked={formik.values.confirmation_msg}
                    onChange={(e) => {
                      formik.setFieldValue('confirmation_msg', e.target.checked)
                      formik.setFieldTouched('confirmation_msg', true, false)
                    }}
                    onBlur={() => formik.setFieldTouched('confirmation_msg', true)}
                  />

                  {t('LABEL.CONSENT_DATA')}
                </label>

                {(formik.touched.confirmation_msg || formik.submitCount > 0) &&
                  formik.errors.confirmation_msg && (
                    <div className='text-danger mt-2'>{String(formik.errors.confirmation_msg)}</div>
                  )}
              </div>
            </section>

            <div className='d-flex flex-column flex-sm-row justify-content-end gap-2 pt-2'>
              <button
                type='submit'
                className='btn btn-primary btn-sm px-sm-5 w-100 w-sm-auto fw-bold'
                disabled={formik.isSubmitting || loading}
              >
                {loading ? (
                  <span className='d-inline-flex align-items-center fw-bold'>
                    <span className='spinner-border spinner-border-sm' aria-hidden='true'></span>
                    {t('GLOBAL.LOADING')}
                  </span>
                ) : (
                  <span className='d-inline-flex align-items-center gap-2'>
                    <span className='fas fa-paper-plane mx-2' />
                    {t('BUTTONS.SUBMIT_APP')}
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default OnlineMembership
